import React, { useState } from 'react';

import { Anchor, Button, Group, LoadingOverlay, Modal, Stack, Text, Title } from '@mantine/core';
import { useShallowEffect } from '@mantine/hooks';
import { IconPlus } from '@tabler/icons-react';
import PaymentMethodSelect from 'components/form-fields/auto-complete-fields/PaymentMethodSelect';
import KiloAlert from 'components/KiloAlert';
import AsyncAddPaymentMethod from 'components/payment-methods/AsyncAddPaymentMethod';
import { isAthleteUnlockablePaymentMethod } from 'components/payment-methods/UnlockPaymentMethodModal';
import SharedPaymentMethodsAlert from 'components/SharedPaymentMethodsAlert';
import useAthleteIsSharingPaymentMethods from 'hooks/athlete/useAthleteIsSharingPaymentMethods';
import useCollectInvoice from 'hooks/invoice/useCollectInvoice';
import useInvoice from 'hooks/invoice/useInvoice';
import { useTracking } from 'hooks/mixpanel/useTracking';
import usePaymentMethod from 'hooks/paymentMethod/usePaymentMethod';
import { NonStripePaymentMethod } from 'types/enums';
import { capitalize } from 'utils/formatters';

import InvoiceSummaryCard from './InvoiceSummaryCard';

interface InvoiceCollectModalProps {
  invoiceId: string;
  opened: boolean;
  close: () => void;
  verb?: 'collect' | 'pay';
  /**
   * Whether this modal is being shown to gym staff, forwarded to the add-card form so the
   * auto-pay toggle is offered here too. Off by default: the modal is also rendered by the
   * athlete's own Invoice Detail page, and the toggle is staff-only.
   */
  isStaffContext?: boolean;
}

const InvoiceCollectModal = ({
  invoiceId,
  opened,
  close,
  verb = 'collect',
  isStaffContext,
}: InvoiceCollectModalProps): JSX.Element => {
  const tracking = useTracking();
  const collectInvoiceMutation = useCollectInvoice();

  const title = verb === 'collect' ? 'Collect Invoice' : 'Pay Invoice';
  const noun = verb === 'collect' ? 'collection' : 'payment';
  const pastTense = verb === 'collect' ? 'collected' : 'paid';

  const [paymentMethod, setPaymentMethod] = useState<string | null>('');
  const [isAddPayment, setIsAddPayment] = useState<boolean>(false);
  const [submissionState, setSubmissionState] = useState<'success' | 'fail' | null>(null);
  const [response, setResponse] = useState<string>('');

  const { data, isLoading, isSuccess } = useInvoice(invoiceId);
  const { data: paymentMethodData } = usePaymentMethod(paymentMethod || undefined);
  const isSharingPaymentMethods = useAthleteIsSharingPaymentMethods(data?.data?.athlete?.athleteId);

  const handleCollect = (): void => {
    if (data?.data) {
      const useNonStripePaymentMethod = Object.values(NonStripePaymentMethod).includes(
        paymentMethod as NonStripePaymentMethod,
      );
      collectInvoiceMutation.mutate(
        {
          invoiceId: data.data.invoiceId,
          paymentMethod: useNonStripePaymentMethod ? null : paymentMethod,
          nonStripePaymentMethod: useNonStripePaymentMethod ? paymentMethod : null,
        },
        {
          onError: error => {
            setSubmissionState('fail');
            setResponse(`Invoice ${noun} failed. ${error.response?.data.message}`);
          },
          onSuccess: res => {
            setSubmissionState('success');
            if (res.data.status === 'PENDING') {
              setResponse(`Invoice ${noun} in progress, no further action needed.`);
            } else {
              setResponse(`Invoice ${pastTense} successfully!`);
            }

            tracking.trackInvoiceCollected(
              data?.data.invoiceId,
              data?.data.athlete.athleteId,
              data?.data.total / 100, // Convert from cents to dollars
              data?.data.nonStripePaymentMethod || data?.data.paymentMethod?.type,
              data?.data.status,
              res?.data?.status,
            );
          },
        },
      );
    }
  };

  const handleClose = (): void => {
    setPaymentMethod('');
    setIsAddPayment(false);
    setSubmissionState(null);
    setResponse('');
    close();
  };

  useShallowEffect(() => {
    if (isSuccess && data?.data) {
      // A soft-deleted PM (the card_present record on an unsettled tap sale) can't be
      // charged again and isn't in the select's options, so don't pre-select it.
      const pm = data.data.paymentMethod;
      const nonStripe = data.data.nonStripePaymentMethod;
      // Unknown isn't an option in the select, so it isn't pre-selected.
      const isSelectableNonStripe = Object.values(NonStripePaymentMethod).includes(
        nonStripe as NonStripePaymentMethod,
      );
      setPaymentMethod(
        (pm && !pm.deletedAt ? pm.paymentMethodId : null) || (isSelectableNonStripe ? nonStripe : null),
      );
    }
  }, [isSuccess, data?.data, opened]);

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={<Title order={4}>{title}</Title>}
      trapFocus
      centered
      size='lg'
    >
      <LoadingOverlay visible={isLoading} overlayBlur={2} />
      {!isAddPayment && isSuccess && data?.data && (
        <>
          <Stack px='md' w='100%' align='center' spacing='xl'>
            <InvoiceSummaryCard invoice={data.data} />
            {submissionState !== 'success' && (
              <>
                <Stack spacing='xs' miw={350}>
                  <PaymentMethodSelect
                    athleteId={data.data.athlete.athleteId}
                    allowCash={verb !== 'pay'}
                    required
                    label='Payment Method'
                    placeholder='Select a payment method'
                    value={paymentMethod}
                    onChange={setPaymentMethod}
                    withinPortal
                    error={
                      paymentMethodData?.data.isLocked
                        ? 'The payment method above has been locked due to previous failed payment attempts.'
                        : undefined
                    }
                  />
                  {verb === 'pay' &&
                    paymentMethodData?.data.isLocked &&
                    isAthleteUnlockablePaymentMethod(paymentMethodData?.data.failureCode || '') && (
                      <KiloAlert variantStyle='default'>
                        To pay using the payment method above, you need to unlock it. You can do this in the{' '}
                        <Anchor href='/account' color='kiloBlue.9'>
                          payment methods section
                        </Anchor>
                        .
                      </KiloAlert>
                    )}
                  <Button
                    leftIcon={<IconPlus size={'1rem'} />}
                    w='fit-content'
                    onClick={() => setIsAddPayment(true)}
                    variant='subtle'
                  >
                    Add Payment Method
                  </Button>
                  {isSharingPaymentMethods && <SharedPaymentMethodsAlert sx={{ marginBottom: '1rem' }} />}
                </Stack>
                <Text fw={500}>{`Are you sure you want to ${verb} this invoice?`}</Text>
              </>
            )}
            {response && (
              <KiloAlert variantStyle={submissionState === 'success' ? 'success' : 'error'}>
                {response}
              </KiloAlert>
            )}
          </Stack>
          {submissionState !== 'success' && (
            <Group position='right' pt='md'>
              <Button
                color='green'
                onClick={handleCollect}
                disabled={isLoading || !paymentMethod || paymentMethodData?.data.isLocked}
                loading={collectInvoiceMutation.isLoading}
              >
                {capitalize(verb)}
              </Button>
            </Group>
          )}
        </>
      )}
      {isAddPayment && isSuccess && data?.data && (
        <AsyncAddPaymentMethod
          athleteId={data.data.athlete.athleteId}
          gymId={data.data.gym}
          isStaffContext={isStaffContext}
          successHandler={(value: string) => {
            setPaymentMethod(value);
            setIsAddPayment(false);
          }}
          cancelHandler={() => setIsAddPayment(false)}
        />
      )}
    </Modal>
  );
};

export default InvoiceCollectModal;
